import { DataStudioParksList, DEVICE_TYPES, IDataStudioPark } from "app/data-studio-redesign/interfaces/data-studio.interface";
import { DimensionService } from 'app/analyze/service/dimension-service/dimension.service';

const hardcodedTurbineSignals = [
  {
    "name": "LPHD1_PhyHealth_stVal_avg",
    "displayName": "Health",
    "displayShortName": "PhyHealthAvg",
    "signalGroupType": "technical",
    "description": "Physical health of the wind turbine",
    "unit": "-"
  }
]

export class DataStudioModel {
  static mapDataStudioParks(parks: DataStudioParksList[], currency: string): IDataStudioPark[] {
    return parks.map(park => ({
      ...park,
      devices: [
        {
          code: park.fe_name,
          name: park.name,
          selected: false,
          serialNav: null,
          queryData: 'parkdata',
          deviceType: DEVICE_TYPES.PARK,
          type: '',
          parkName: park.fe_name,
          _id: park._id,
          signalsList: park.signalList.map(signal => ({
            ...signal,
            unit: (currency !== '€' && signal.unit?.includes('€'))
              ? signal.unit.replace(/€/g, currency)
              : signal.unit,
            selected: false
          }))
        },
        ...park.turbines.map(turbine => ({
          ...turbine,
          queryData: 'scada',
          parkName: park.fe_name,
          deviceType: DEVICE_TYPES.TURBINE,
          signalsList: [...turbine.signalsList, ...hardcodedTurbineSignals].map(signal => ({
            ...signal,
            selected: false
          })),
          selected: false
        })).sort((a, b) => a.code.localeCompare(b.code, undefined, {numeric: true, sensitivity: 'base'})),
        ...park.controlSystem.map(device => ({
          ...device,
          queryData: 'scada',
          deviceType: DEVICE_TYPES.CONTROL_SYSTEM,
          parkName: park.fe_name,
          signalsList: device.signalsList.map(signal => ({
            ...signal,
            signalGroupType: 'controlSystem',
            selected: false
          })),
          selected: false
        })).sort((a, b) => a.code.localeCompare(b.code, undefined, {numeric: true, sensitivity: 'base'})),
        ...park.metmasts?.flatMap(metmast => metmast.sensorsList.map(sensor => ({
          _id: sensor._id,
          name: `${metmast.name} | ${sensor.name}`,
          code: sensor.name,
          queryData: 'scada',
          deviceType: DEVICE_TYPES.METMAST,
          parkName: park.fe_name,
          signalsList: sensor.signalsList.map(signal => ({
            name: signal.dbName,
            displayName: signal.displayName,
            unit: signal.unit,
            signalGroupType: DEVICE_TYPES.METMAST,
            selected: false
          })),
          selected: false
        }))).sort((a, b) => a.code.localeCompare(b.code, undefined, {numeric: true, sensitivity: 'base'}))
      ],
      selected: false
    })).sort((a, b) => a.name.localeCompare(b.name, undefined, {numeric: true, sensitivity: 'base'}));
  }

  static queryListMapper(data, dimensionService: DimensionService, constrolSystems = [], metmastSensorList = []) {
    const assetMapper = {...DataStudioModel.getAssetMapper(dimensionService, constrolSystems, metmastSensorList)};
    return {
      totalCount: data.data.portfolios[0].dataAnalysisQueries.totalCount,
      queries: data.data.portfolios[0].dataAnalysisQueries.dataAnalysisQueries.map(
        query => {
          const parks = query.parkIds?.map(id => assetMapper[id]).join(', ') || '';
          const assets = [...(query.assetIds || []), ...(query.assetIdYs || [])]?.map(id => assetMapper[id]).join(', ') || '';
          return {...query, sites: parks, assets}
        }
      )
    }
  }

  static getAssetMapper(dimensionService, constrolSystems, metmastSensorList) {
    const assetMapper = {};
    dimensionService.turbineList.forEach(turbine => {
      assetMapper[turbine._id] = turbine.name.replace(/_/g, ' ');
    });
    dimensionService.parkList.forEach(park => {
      assetMapper[park.id] = park.frontEndCode;
    });
    constrolSystems.forEach(cs => {
      assetMapper[cs._id] = cs.name.replace(/_/g, ' ');
    });
    metmastSensorList.forEach(metmastSensor => {
      assetMapper[metmastSensor._id] = metmastSensor?.name?.replace(/_/g, ' ');
    });

    return assetMapper;
  }

  static queryListMapperForReport(data) {
    return data.data.portfolios[0].dataAnalysisQueries.dataAnalysisQueries.map(query => ({...query})).sort((a, b) => b.queryNumber - a.queryNumber);
  }

  static queryDetailsMapper(data,dimensionService: DimensionService, constrolSystems = [], metmastSensorList = []) {
    const assetMapper = {...DataStudioModel.getAssetMapper(dimensionService, constrolSystems, metmastSensorList)};
    return {
      id: data.queryNumber,
      name: data.title,
      period: data.period,
      granularity: data.granularity,
      startTs: data.startTs,
      endTs: data.endTs,
      numberOfSeries: data.numberOfSeries,
      series: data.series.map((serie, index) => {
        const deviceTypeY = serie.deviceTypeY || serie.deviceType;
        const assetIdY = serie.assetIdY || serie.assetId;
        const idX = `${serie.deviceCode || ''}${serie.deviceType === DEVICE_TYPES.METMAST ? '_' + assetMapper[serie.assetId].replace(/ /g, '_') : ''}`
        const idY = serie.assetIdY ? `_${serie.deviceCodeY || ''}${deviceTypeY === DEVICE_TYPES.METMAST ? '_' + assetMapper[assetIdY].replace(/ /g, '_') : ''}` : '';
        return {
          assetId: serie.assetId,
          assetIdY,
          chartData: [],
          chartTypeName: serie.graphType,
          color: serie.colorHex,
          dashStyle: serie.graphDashStyle,
          device: assetMapper[serie.assetId] || assetMapper[serie.parkId],
          deviceY: assetMapper[assetIdY] || assetMapper[serie.parkId],
          deviceName: serie.deviceName,
          deviceNameY: serie.deviceNameY || serie.deviceName,
          deviceType: serie.deviceType,
          deviceTypeY,
          granularity: serie.granularity,
          id: `${idX}${idY}_${serie.signal}${serie.signalY ? '_' + serie.signalY : ''}`,
          marker: serie.graphDataMarker,
          newItem: false,
          parkDisplayName: serie.parkDisplayName,
          parkId: serie.parkId,
          parkName: serie.parkName,
          queryData: serie.queryDataType,
          signal: serie.signal,
          signalName: serie.signalName,
          signalNameY: serie.signalNameY,
          signalY: serie.signalY,
          step: serie.graphIsStep,
          type: serie.graphDataType,
          unit: serie.signalUnit,
          unitY: serie.signalUnitY,
          visible: serie.visible,
          yAxis: index,
          yAxisMax: serie.yAxisMax,
          yAxisMin: serie.yAxisMin,
        }
      })
    }
  }

  static createQuerySeriesMapper(series) {
    return series.map(serie => {
      const {
        aggregation,
        assetId,
        assetIdY,
        colorHex,
        granularity,
        graphDashStyle,
        graphDataMarker,
        graphDataType,
        graphIsStep,
        graphType,
        queryDataType,
        signal,
        signalY,
        signalName,
        signalNameY,
        signalUnit,
        signalUnitY,
        visible,
        parkId,
        deviceType,
        deviceTypeY
      } = serie;
      return {
        aggregation,
        assetId,
        assetIdY,
        colorHex,
        granularity,
        graphDashStyle,
        graphDataMarker: {
          symbol: graphDataMarker.symbol
        },
        graphDataType,
        graphIsStep,
        graphType,
        queryDataType,
        signal,
        signalY,
        signalName,
        signalNameY,
        signalUnit,
        signalUnitY,
        visible,
        parkId,
        deviceType,
        deviceTypeY
      }
    })
  }
}
